import { Feature } from 'ol';
import { getCenter } from 'ol/extent';
import { Geometry, Point } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import {
    featureTooSmall,
    isPointFeature,
    roadObjectToFeature,
} from '@/utils/SpatialHelper';
import { FeatureColorKey, FeatureLayerType } from './FeatureLayerType';

export interface CachedFeature {
    feature: Feature<Geometry>;
    geometry: Geometry;
    tinyFeature?: Feature<Geometry>;
}

type FeatureFactory = typeof roadObjectToFeature;

export class VegobjekterFeatureCache {
    private cache = new WeakMap<Vegobjekt, Map<string, CachedFeature | null>>();

    constructor(private readonly createFeature: FeatureFactory = roadObjectToFeature) {}

    get(roadObject: Vegobjekt, wkt: string, color: string): CachedFeature | undefined {
        let geometries = this.cache.get(roadObject);
        if (!geometries) {
            geometries = new Map();
            this.cache.set(roadObject, geometries);
        }

        let cached = geometries.get(wkt);
        if (cached === undefined) {
            try {
                const feature = this.createFeature(
                    roadObject,
                    wkt,
                    FeatureLayerType.FEATURE_VEGOBJEKT,
                    color
                );
                const geometry = feature.getGeometry();
                cached = geometry ? { feature, geometry } : null;
            } catch {
                cached = null;
            }
            geometries.set(wkt, cached);
        }

        if (!cached) return undefined;

        cached.feature.set(FeatureColorKey, color);
        cached.feature.setGeometry(cached.geometry);
        return cached;
    }

    clear(): void {
        this.cache = new WeakMap();
    }
}

export function splitCachedFeatures(
    features: readonly CachedFeature[],
    transformTinyGeometries: boolean,
    resolution: number
): { points: Feature<Geometry>[]; nonPoints: Feature<Geometry>[] } {
    const points: Feature<Geometry>[] = [];
    const nonPoints: Feature<Geometry>[] = [];

    features.forEach(cached => {
        const feature = cached.feature;
        feature.setGeometry(cached.geometry);

        if (isPointFeature(feature)) {
            points.push(feature);
        } else if (transformTinyGeometries && featureTooSmall(feature, resolution)) {
            if (!cached.tinyFeature) {
                cached.tinyFeature = feature.clone();
                cached.tinyFeature.setId(feature.getId());
                cached.tinyFeature.setGeometry(
                    new Point(getCenter(cached.geometry.getExtent()))
                );
            }
            cached.tinyFeature.set(FeatureColorKey, feature.get(FeatureColorKey));
            points.push(cached.tinyFeature);
        } else {
            nonPoints.push(feature);
        }
    });

    return { points, nonPoints };
}

export function updateVectorSourceFeatures(
    source: VectorSource,
    previousFeatures: readonly Feature<Geometry>[],
    nextFeatures: readonly Feature<Geometry>[]
): void {
    const nextFeatureSet = new Set(nextFeatures);
    const removed = previousFeatures.filter(feature => !nextFeatureSet.has(feature));
    if (removed.length > 0) source.removeFeatures(removed);

    const previousFeatureSet = new Set(previousFeatures);
    const added = nextFeatures.filter(feature => !previousFeatureSet.has(feature));
    if (added.length > 0) source.addFeatures(added);
}
